import {appConfig} from "../config";
import {AttributeFilter, Employee} from "./EmployeeApi";
import {Helper} from "./Helper";
import {apiRequest} from "../util/ApiUtils";
import {UserAccount} from "./UserAccountApi";
import {ApiResponse} from "./types";
import {ApiClient} from "./apiClient";
import {TimeRecordRequest} from "./RequestApi";

export interface ClockInRequest {
    note?: string;
}

export interface ClockOutRequest {
    note?: string;
}

export interface Timesheet {
    employeeId: string;
    employee: Employee;
    employeeName: string;
    totalWorkingMinutes: number;
    totalHours: string;
    totalPaidHours: string;
    firstWorkingDay: Date;
    lastWorkingDay: Date;
    entries: TimesheetEntry[];
}

export interface TimesheetEntry {
    employeeId: string;
    date: Date;
    durationMinutes: number;
    hours: string;
    absenceTypeId: number;
    comment: string;
}

export interface TimeRecord {
    employee: Employee;
    recordDate: string; // ISO date string
    revision: number;

    createdByUser?: UserAccount;
    status: TimeRecordStatus;

    entries: TimeRecordEntry[];

    // Cached aggregates
    targetMinutes?: number;
    actualMinutes?: number;
    breakMinutes?: number;
    overtimeMinutes?: number;

    actualSeconds?: number;

    // Day classification
    isHoliday: boolean;
    holidayName?: string;
    isWeekend: boolean;
    isAbsence: boolean;
    absenceType?: string;
    leaveRequestId?: string;
    notes?: string;

    // Audit
    recordedAt: Date;
    updatedAt: Date;

    // Submission / approval
    submittedAt?: Date;
    approvedAt?: Date;
}

export interface TimeRecordEntry {
    id: string;
    timeRecordEntryDate: string; // ISO date (YYYY-MM-DD)
    startTimestamp: string;
    endTimestamp: string;
    localStartTime: string;
    localEndTime: string;
    durationMinutes: number;
    durationSeconds: number;

    entryType: TimeRecordEntryType;

    isManualEntry: boolean;
    isCorrected: boolean;
    open: boolean;
    allocated: boolean;
}

export type TimeRecordEntryType = "WORK" | "BREAK" | "ABSENCE";

export enum TimeRecordStatus {
    DRAFT = 'DRAFT',
    SUBMITTED = 'SUBMITTED',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    LOCKED = 'LOCKED',
    EXPORTED = 'EXPORTED',
    CLOCKED_IN = 'CLOCKED_IN',
    CLOCKED_OUT = 'CLOCKED_OUT'
}

export interface TimesheetsRetrieveRequest {
    year: number;
    month: number;
    filters: AttributeFilter[];
    page?: number;
}

export interface TimesheetsRetrieveResponse {
    content: Timesheet[];
    page: number,
    size: number,
    totalElements: number,
    totalPages: number
}

export interface CreateTimesheetEntryRequest {
    employeeId: string;
    date: Date;
    hours: number | null;
    minutes: number | null;
    absenceTypeId: number;
    comment?: string | null;
}

export interface DeleteTimesheetEntryRequest {
    employeeId: string;
    date: Date;
}

export type CreateTimesheetEntryResponseCallback = (arg: TimesheetEntry) => void;

export type DeleteTimesheetEntryResponseCallback = (employeeId: string, date: Date) => void;

const backendUrl = appConfig.backendUrl;

export async function retrieveTimesheets(
    requestData: TimesheetsRetrieveRequest
): Promise<TimesheetsRetrieveResponse> {

    const {year, month, filters, page} = requestData;

    const queryParams = new URLSearchParams();

    if (filters?.length) {
        filters.forEach((filter, index) => {
            queryParams.append(`filters[${index}].attributeName`, filter.attributeName);
            queryParams.append(`filters[${index}].operator`, filter.operator);
            if (filter.from) {
                queryParams.append(`filters[${index}].from`, filter.from);
            }
            if (filter.to) {
                queryParams.append(`filters[${index}].to`, filter.to);
            }
            if (filter.value !== undefined && filter.value !== null) {
                queryParams.append(`filters[${index}].value`, String(filter.value));
            }
        });
    }

    if (page !== undefined) {
        queryParams.append('page', page.toString());
    }

    const url = `${backendUrl}/api/timesheets/${year}/${month}?${queryParams.toString()}`;

    const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
    });

    if (!response.ok) {
        throw new Error(`Failed to retrieve timesheets: ${response.statusText}`);
    }

    const data: TimesheetsRetrieveResponse = Helper.Deserialize(await response.text());
    return data as TimesheetsRetrieveResponse;
}


export async function timesheetEntryCreate(request: CreateTimesheetEntryRequest, callback: CreateTimesheetEntryResponseCallback): Promise<void> {

    try {
        const response = await fetch(`${backendUrl}/api/timesheets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(request),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const data: TimesheetEntry = Helper.Deserialize(await response.text());
        callback(data);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function timesheetEntryDelete(request: DeleteTimesheetEntryRequest, callback: DeleteTimesheetEntryResponseCallback): Promise<void> {

    try {
        const response = await fetch(`${backendUrl}/api/timesheets`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(request),
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const employeeId = request.employeeId;
        const date = request.date;
        callback(employeeId, date);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}